import { ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintPageFooter from "@components/print/PrintElements/PrintPageFooter";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import DocumentInfo from "@components/print/PrintElements/DocumentInfo";
import ProductList from "@components/print/PrintElements/ProductList";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import { SettlementFormDataKeys } from "@/generated/SettlementFormDataKeys";
import { SettlementRelatedAttributes } from "@/formTypes/SettlementDataType";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { formatDate } from "@utils/Api";
import { CustomerInfoBase } from "@/formTypes/DocumentClientType";
import { chunkArray } from "@utils/Utils";
import { sortProductsByOrder } from "@utils/EDocument";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";

const SettlementPrint = () => {
  const params = useParams()
  const { id: settlementId, autoPrint = false, copies = 0 } = params;

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const maxItemPerPage = Number(suscription?.items_per_page || 30)
  const [settlement, setSettlement] = useState<SettlementDataType>({})
  const [settlementProdCount, setSettlementProdCount] = useState<number>(0)
  const [settlementsProducts, setSettlementsProducts] = useState<EDocumentProductDataType[]>()
  const [documentPages, setDocumentPages] = useState<EDocumentProductDataType[][]>()

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadSettlementData()
  }, []);

  useEffect(() => {
    const pages = chunkArray(settlementsProducts || [], maxItemPerPage)
    setDocumentPages(pages)
  }, [maxItemPerPage, settlementsProducts]);

  const loadSettlementData = async () => {
    if (settlementId) {
      const settlement = await SettlementApi.get(settlementId, {
        selectionSet: [
          ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "settlementProducts.*",
          "settlementProducts.product.*"
        ]
      })
      setSettlementsProducts(sortProductsByOrder(settlement.settlementProducts))
      setSettlementProdCount(settlement.settlementProducts?.length || 0)
      setSettlement(settlement)
    }
    PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
  }

  function toCustomerInfoBase(p: SettlementDataType): CustomerInfoBase {
    if (!p?.nombre_factura) throw new Error("nombre_factura requerido");
    return {
      nombre_factura: p.nombre_factura,
      razon_social: p.razon_social ?? "",
      tel1: p.tel1 ?? "",
      tel2: p.tel2 ?? "",
      email: p.email ?? "",
      direccion: p.direccion ?? "",
      clientActivitycode: p.clientActivitycode ?? "",
      plazo: p.plazo ?? null,
      client: p.client ?? null,
      tipo_identificacion: p.tipo_identificacion ?? "",
      identificacion: p.identificacion ?? "",
    };
  }

  const renderPage = (documentPages: EDocumentProductDataType[][]): ReactNode[] => {
    return documentPages.map((page, index) => {
      return (
        <PrintPage
          nula={settlement.anulada || false}
          autoPrint={autoPrint === "1"}
          copies={Number(copies)}
        >
          <PrintPageHeader suscription={suscription} />
          <div className="page-content">
            <PrintDocumentHeader title={`Liquidacion #${settlement.numero}`} fecha={formatDate(settlement.fecha, "YYYY-MM-DD", "DD.MM.YY")} />
            <DocumentInfo
              customer={toCustomerInfoBase(settlement)}
              docType="Liquidacion"
            />
            <ProductList
              maxItemPerPage={maxItemPerPage}
              products={page}
              productCount={settlementProdCount}
            />
            <PrintPageFooter
              currencyCode={settlement.currency.currency_id}
              exchangeRate={settlement.exchange_rate || 0}
              comments={[
                `${suscription?.settlement_info}`,
                `${settlement.document_comments}`
              ]}
              summary={{
                subtotal: settlement.subtotal || 0,
                descuento: settlement.descuento || 0,
                impuestos: settlement.impuestos || 0,
                total: settlement.total || 0
              }}
              currentPage={(index + 1)}
              totalPages={documentPages.length} />
          </div>

        </PrintPage>
      )
    });
  }

  return (
    <div>
      {settlement?.id && documentPages && renderPage(documentPages)}
      {!settlement.id && (
        <h3>Cargando </h3>
      )}

    </div>
  );
}

export default SettlementPrint;
